/**
 * =============================================================================
 * HouseCollisionView - Editor de bloqueos (colisión) por tile de las casas
 * =============================================================================
 *
 * Herramienta DOM/Canvas2D (sin Phaser) que se abre desde el login (botón 🏠), al
 * lado del World Builder / Map Editor. Muestra cada casa indexada (`HOUSES` de
 * `shared/world/houseData.ts`) ARMADA —todas sus partes con los offsets de
 * perspectiva + techo— y permite PINTAR por tile los bloqueos que lleva la casa,
 * libremente sobre la casa completa.
 *
 * Los bloqueos se guardan como offsets relativos al ancla (`{dx,dy}`) en el sidecar
 * `shared/world/houseCollision.json` vía el endpoint DEV `__dev/houses/save`. Ese
 * archivo lo consume `stampHouse()`, que usa la lista explícita como colisión (o cae
 * al rectángulo paramétrico si la casa no fue editada).
 *
 * Un checkbox muestra/oculta la CAPA 4 (techos / `roof`) para poder pintar viendo el
 * interior de la casa.
 */

import { TILE_SIZE } from '@shared/constants';
import {
  HOUSES,
  HOUSE_IDS,
  houseWidthTiles,
  houseFootprintDepthTiles,
  type HouseBlockOffset,
  type HouseDef,
} from '@shared/world/houseData';
import { getDecorationAssetUrl } from '../../util/decorationAssets';
import { createButton, createCheckbox, createSelect } from '../../ui/formControls';
import { listHouseCollision, saveHouseCollision } from './houseCollisionClient';

export interface HouseCollisionViewOptions {
  /** Contenedor donde montar la herramienta (se limpia su contenido). */
  host: HTMLElement;
  /** Callback al pulsar "Volver al login". */
  onExit: () => void;
}

/** Margen de tiles extra alrededor de la huella, para poder bloquear un poco por fuera. */
const GRID_MARGIN = 2;

const CANVAS_W = 760;
const CANVAS_H = 620;

export class HouseCollisionView {
  private readonly host: HTMLElement;
  private readonly onExit: () => void;

  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  private currentId: string = HOUSE_IDS[0] ?? '';
  private def: HouseDef | null = null;

  /** Bloqueos de la casa actual, como "dx,dy". */
  private blocked = new Set<string>();
  /** Catálogo cargado del server (houseId → offsets). */
  private serverCatalog: Record<string, HouseBlockOffset[]> = {};

  /** Texturas cargadas por key de parte. */
  private readonly images = new Map<string, HTMLImageElement>();

  /** Atlas del piso interior (floors1.png); se dibuja la celda de madera "Floor 2". */
  private floorImg: HTMLImageElement | null = null;

  private showRoof = true;
  private showFloor = true;
  private showBlocks = true;

  // Ancla de la casa en el espacio de tiles LOCAL del canvas (así el menor dx/dy cae en col/fila 0).
  private ax = 0;
  private ay = 0;
  private gridDxMin = 0;
  private gridDxMax = 0;
  private gridDyMin = 0;
  private gridDyMax = 0;

  // Transform world→canvas (calculado por fitView).
  private zoom = 1;
  private offX = 0;
  private offY = 0;

  private isPainting = false;
  private paintValue = true; // true = agregar bloqueo, false = borrar

  private statusEl!: HTMLSpanElement;
  private countEl!: HTMLSpanElement;

  constructor(opts: HouseCollisionViewOptions) {
    this.host = opts.host;
    this.onExit = opts.onExit;
    this.host.innerHTML = '';

    const root = document.createElement('div');
    root.style.position = 'fixed';
    root.style.inset = '0';
    root.style.background = '#1c1c1c';
    root.style.color = '#eee';
    root.style.display = 'flex';
    root.style.flexDirection = 'column';
    root.style.fontFamily = 'system-ui, sans-serif';
    this.host.appendChild(root);

    root.appendChild(this.buildToolbar());

    const stage = document.createElement('div');
    stage.style.flex = '1';
    stage.style.display = 'flex';
    stage.style.alignItems = 'center';
    stage.style.justifyContent = 'center';
    stage.style.overflow = 'hidden';
    root.appendChild(stage);

    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_W;
    this.canvas.height = CANVAS_H;
    this.canvas.style.background = '#2b2b2b';
    this.canvas.style.border = '1px solid #444';
    this.canvas.style.borderRadius = '6px';
    this.canvas.style.cursor = 'crosshair';
    stage.appendChild(this.canvas);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('No 2D context');
    this.ctx = ctx;
    this.ctx.imageSmoothingEnabled = false;

    this.wireCanvasEvents();

    // Cargar catálogo del server y arrancar en la primera casa.
    void this.init();
  }

  private async init(): Promise<void> {
    // Atlas del piso interior de casa (la celda de madera "Floor 2" = gridX4/gridY0 de floors1).
    const floor = new Image();
    floor.onload = () => this.render();
    floor.src = '/assets/world/floors/floors1.png';
    this.floorImg = floor;
    try {
      this.serverCatalog = await listHouseCollision();
    } catch (err) {
      this.setStatus(`No se pudo leer el server (¿DEV apagado?): ${(err as Error).message}`, true);
      this.serverCatalog = {};
    }
    if (this.currentId) this.selectHouse(this.currentId);
  }

  // ── Toolbar ────────────────────────────────────────────────────────────

  private buildToolbar(): HTMLElement {
    const bar = document.createElement('div');
    bar.style.display = 'flex';
    bar.style.alignItems = 'center';
    bar.style.gap = '12px';
    bar.style.padding = '10px 14px';
    bar.style.background = 'rgba(0,0,0,0.4)';
    bar.style.borderBottom = '1px solid #333';
    bar.style.flexWrap = 'wrap';

    const title = document.createElement('strong');
    title.textContent = '🏠 Bloqueos de casas';
    title.style.color = '#FFD700';
    bar.appendChild(title);

    // Selector de casa.
    const houseSelect = createSelect({
      options: HOUSE_IDS.map((id) => ({ value: id, label: HOUSES[id]?.name ?? id })),
      value: this.currentId,
    });
    houseSelect.select.addEventListener('change', () => this.selectHouse(houseSelect.select.value));
    bar.appendChild(houseSelect.wrapper);

    // Checkbox mostrar/ocultar techo (capa 4).
    const roofChk = createCheckbox({ label: 'Mostrar techo (capa 4)', checked: this.showRoof });
    roofChk.input.addEventListener('change', () => {
      this.showRoof = roofChk.input.checked;
      this.render();
    });
    bar.appendChild(roofChk.wrapper);

    // Checkbox mostrar/ocultar el piso interior (madera).
    const floorChk = createCheckbox({ label: 'Mostrar piso', checked: this.showFloor });
    floorChk.input.addEventListener('change', () => {
      this.showFloor = floorChk.input.checked;
      this.render();
    });
    bar.appendChild(floorChk.wrapper);

    // Checkbox mostrar/ocultar los bloqueos (rojo) — útil para ver el piso limpio.
    const blocksChk = createCheckbox({ label: 'Mostrar bloqueos', checked: this.showBlocks });
    blocksChk.input.addEventListener('change', () => {
      this.showBlocks = blocksChk.input.checked;
      this.render();
    });
    bar.appendChild(blocksChk.wrapper);

    // Contador de bloqueos.
    this.countEl = document.createElement('span');
    this.countEl.style.opacity = '0.85';
    bar.appendChild(this.countEl);

    const spacer = document.createElement('div');
    spacer.style.flex = '1';
    bar.appendChild(spacer);

    this.statusEl = document.createElement('span');
    this.statusEl.style.fontSize = '12px';
    this.statusEl.style.opacity = '0.85';
    bar.appendChild(this.statusEl);

    // Resetear al rectángulo por defecto.
    const resetBtn = createButton('Resetear a rectángulo', { variant: 'ghost' });
    resetBtn.addEventListener('click', () => {
      if (this.def) {
        this.blocked = this.rectFallbackOffsets(this.def);
        this.refreshCount();
        this.render();
        this.setStatus('Reseteado al rectángulo por defecto (sin guardar).');
      }
    });
    bar.appendChild(resetBtn);

    const clearBtn = createButton('Vaciar', { variant: 'ghost' });
    clearBtn.addEventListener('click', () => {
      this.blocked.clear();
      this.refreshCount();
      this.render();
    });
    bar.appendChild(clearBtn);

    const saveBtn = createButton('Guardar');
    saveBtn.addEventListener('click', () => void this.save(saveBtn));
    bar.appendChild(saveBtn);

    const backBtn = createButton('Volver al login', { variant: 'ghost' });
    backBtn.addEventListener('click', () => this.onExit());
    bar.appendChild(backBtn);

    return bar;
  }

  // ── Carga de casa ────────────────────────────────────────────────────────

  private selectHouse(id: string): void {
    const def = HOUSES[id];
    if (!def) return;
    this.currentId = id;
    this.def = def;

    // Bounds de la grilla (offsets), derivados de la huella + margen.
    const halfVisual = Math.ceil(houseWidthTiles(def) / 2);
    const maxBack = def.parts.reduce((m, p) => Math.max(m, p.tilesBack), 0);
    this.gridDxMin = -(halfVisual + GRID_MARGIN);
    this.gridDxMax = halfVisual + GRID_MARGIN;
    this.gridDyMin = -(maxBack + GRID_MARGIN);
    this.gridDyMax = GRID_MARGIN;
    // Ancla en coords locales: el menor dx/dy cae en col/fila 0.
    this.ax = -this.gridDxMin;
    this.ay = -this.gridDyMin;

    // Bloqueos iniciales: los del server si existen; si no, sembrar del rectángulo.
    const fromServer = this.serverCatalog[id];
    if (fromServer && fromServer.length > 0) {
      this.blocked = new Set(fromServer.map((o) => `${o.dx},${o.dy}`));
    } else {
      this.blocked = this.rectFallbackOffsets(def);
    }
    this.refreshCount();

    // Cargar texturas de las partes (lazy) y renderizar a medida que llegan.
    for (const part of def.parts) {
      if (this.images.has(part.key)) continue;
      const img = new Image();
      img.onload = () => {
        if (this.currentId === id) this.fitAndRender();
      };
      img.src = getDecorationAssetUrl(part.key);
      this.images.set(part.key, img);
    }

    this.fitAndRender();
  }

  /** Offsets del rectángulo paramétrico por defecto (misma lógica que stampHouse sin sidecar). */
  private rectFallbackOffsets(def: HouseDef): Set<string> {
    // stampHouse usa houseBlockedOffsets(def.id) internamente; si el bundle tuviera datos,
    // devolvería esos. Para el "reset" queremos SIEMPRE el rectángulo, así que lo derivamos acá.
    const depth = houseFootprintDepthTiles(def);
    const halfWidth = def.collision?.halfWidth ?? Math.floor(houseWidthTiles(def) / 2);
    const frontTb = def.collision?.front ?? 0;
    const backTb = def.collision?.back ?? depth - 1;
    const left = -halfWidth;
    const right = halfWidth;
    const front = -frontTb;
    const back = -backTb;
    const doorCols = new Set((def.doorCols ?? [1, 2]).map((c) => c));
    const out = new Set<string>();
    for (let dx = left; dx <= right; dx++) {
      for (let dy = back; dy <= front; dy++) {
        const isPerimeter = dx === left || dx === right || dy === front || dy === back;
        const isDoor = dy === front && doorCols.has(dx);
        if (isPerimeter && !isDoor) out.add(`${dx},${dy}`);
      }
    }
    return out;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  /** Calcula el transform world→canvas para encuadrar casa + grilla, y renderiza. */
  private fitAndRender(): void {
    if (!this.def) return;

    // Bounds del contenido en world px (grilla + imágenes de partes cargadas).
    let minX = (this.ax + this.gridDxMin) * TILE_SIZE;
    let maxX = (this.ax + this.gridDxMax + 1) * TILE_SIZE;
    let minY = (this.ay + this.gridDyMin) * TILE_SIZE;
    let maxY = (this.ay + this.gridDyMax + 1) * TILE_SIZE;

    for (const part of this.def.parts) {
      const img = this.images.get(part.key);
      if (!img || !img.complete || img.naturalWidth === 0) continue;
      const cx = this.ax * TILE_SIZE + TILE_SIZE / 2;
      const bottomY = (this.ay - part.tilesBack) * TILE_SIZE + TILE_SIZE;
      minX = Math.min(minX, cx - img.naturalWidth / 2);
      maxX = Math.max(maxX, cx + img.naturalWidth / 2);
      minY = Math.min(minY, bottomY - img.naturalHeight);
      maxY = Math.max(maxY, bottomY);
    }

    const contentW = maxX - minX;
    const contentH = maxY - minY;
    this.zoom = Math.min(CANVAS_W / contentW, CANVAS_H / contentH) * 0.92;
    this.offX = (CANVAS_W - contentW * this.zoom) / 2 - minX * this.zoom;
    this.offY = (CANVAS_H - contentH * this.zoom) / 2 - minY * this.zoom;

    this.render();
  }

  private render(): void {
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    if (!this.def) return;

    ctx.setTransform(this.zoom, 0, 0, this.zoom, this.offX, this.offY);

    // 0) Piso interior (madera), DEBAJO de las paredes — igual que en el juego
    //    (layer1_ground bajo ground_deco_over). Ver GameRoom/houseData floor.
    if (this.showFloor) this.drawFloor();

    // 1) Partes del cuerpo (atrás→adelante) y luego techo, respetando el checkbox de capa 4.
    const body = this.def.parts
      .filter((p) => p.layer !== 'roof')
      .sort((a, b) => b.tilesBack - a.tilesBack);
    const roofs = this.def.parts.filter((p) => p.layer === 'roof');
    for (const part of body) this.drawPart(part.key, part.tilesBack);
    if (this.showRoof) {
      ctx.globalAlpha = 0.85; // techo semi-translúcido para ver el interior aun mostrándolo
      for (const part of roofs) this.drawPart(part.key, part.tilesBack);
      ctx.globalAlpha = 1;
    }

    // 2) Grilla de tiles.
    ctx.lineWidth = 1 / this.zoom;
    ctx.strokeStyle = 'rgba(255,255,255,0.18)';
    for (let dx = this.gridDxMin; dx <= this.gridDxMax + 1; dx++) {
      const wx = (this.ax + dx) * TILE_SIZE;
      ctx.beginPath();
      ctx.moveTo(wx, (this.ay + this.gridDyMin) * TILE_SIZE);
      ctx.lineTo(wx, (this.ay + this.gridDyMax + 1) * TILE_SIZE);
      ctx.stroke();
    }
    for (let dy = this.gridDyMin; dy <= this.gridDyMax + 1; dy++) {
      const wy = (this.ay + dy) * TILE_SIZE;
      ctx.beginPath();
      ctx.moveTo((this.ax + this.gridDxMin) * TILE_SIZE, wy);
      ctx.lineTo((this.ax + this.gridDxMax + 1) * TILE_SIZE, wy);
      ctx.stroke();
    }

    // 3) Celdas bloqueadas (ocultables para ver el piso limpio).
    if (this.showBlocks) for (const key of this.blocked) {
      const [dx, dy] = key.split(',').map(Number);
      const wx = (this.ax + dx) * TILE_SIZE;
      const wy = (this.ay + dy) * TILE_SIZE;
      ctx.fillStyle = 'rgba(220,50,50,0.45)';
      ctx.fillRect(wx, wy, TILE_SIZE, TILE_SIZE);
      ctx.strokeStyle = 'rgba(255,90,90,0.9)';
      ctx.lineWidth = 1.5 / this.zoom;
      ctx.strokeRect(wx + 0.5, wy + 0.5, TILE_SIZE - 1, TILE_SIZE - 1);
    }

    // 4) Marca del ancla (base front-centro): tile (0,0).
    const ox = this.ax * TILE_SIZE;
    const oy = this.ay * TILE_SIZE;
    ctx.strokeStyle = 'rgba(255,215,0,0.9)';
    ctx.lineWidth = 2 / this.zoom;
    ctx.strokeRect(ox + 1, oy + 1, TILE_SIZE - 2, TILE_SIZE - 2);
  }

  private drawPart(key: string, tilesBack: number): void {
    const img = this.images.get(key);
    if (!img || !img.complete || img.naturalWidth === 0) return;
    const cx = this.ax * TILE_SIZE + TILE_SIZE / 2;
    const bottomY = (this.ay - tilesBack) * TILE_SIZE + TILE_SIZE;
    this.ctx.drawImage(img, cx - img.naturalWidth / 2, bottomY - img.naturalHeight);
  }

  /**
   * Dibuja el piso interior de madera en la MISMA región que estampa el juego
   * (`stampHouse` floor: interior de la colisión = `left+1..right-1 × back+2..front+1`),
   * con la celda "Floor 2" de floors1 (gridX4/gridY0) partida en sub-tiles 1×1 por
   * `(dx,dy) mod 4` — idéntico a `houseFloorSprite`. Va bajo las paredes.
   */
  private drawFloor(): void {
    const def = this.def;
    const img = this.floorImg;
    if (!def || !img || !img.complete || img.naturalWidth === 0) return;
    const halfWidth = def.collision?.halfWidth ?? Math.floor(houseWidthTiles(def) / 2);
    const frontTb = def.collision?.front ?? 0;
    const backTb = def.collision?.back ?? houseFootprintDepthTiles(def) - 1;
    const dxL = -halfWidth + 1;
    const dxR = halfWidth - 1;
    const dyTop = -backTb + 2;
    const dyBot = -frontTb + 1;
    for (let dx = dxL; dx <= dxR; dx++) {
      for (let dy = dyTop; dy <= dyBot; dy++) {
        // Sub-tile de la celda de madera (gridX 4..7, gridY 0..3) por fase.
        const sx = (4 + (((dx % 4) + 4) % 4)) * TILE_SIZE;
        const sy = (((dy % 4) + 4) % 4) * TILE_SIZE;
        const wx = (this.ax + dx) * TILE_SIZE;
        const wy = (this.ay + dy) * TILE_SIZE;
        this.ctx.drawImage(img, sx, sy, TILE_SIZE, TILE_SIZE, wx, wy, TILE_SIZE, TILE_SIZE);
      }
    }
  }

  // ── Interacción ────────────────────────────────────────────────────────────

  private wireCanvasEvents(): void {
    const toOffset = (ev: MouseEvent): { dx: number; dy: number } | null => {
      const rect = this.canvas.getBoundingClientRect();
      const px = (ev.clientX - rect.left) * (CANVAS_W / rect.width);
      const py = (ev.clientY - rect.top) * (CANVAS_H / rect.height);
      const wx = (px - this.offX) / this.zoom;
      const wy = (py - this.offY) / this.zoom;
      const dx = Math.floor(wx / TILE_SIZE) - this.ax;
      const dy = Math.floor(wy / TILE_SIZE) - this.ay;
      if (dx < this.gridDxMin || dx > this.gridDxMax || dy < this.gridDyMin || dy > this.gridDyMax) {
        return null;
      }
      return { dx, dy };
    };

    this.canvas.addEventListener('mousedown', (ev) => {
      const off = toOffset(ev);
      if (!off) return;
      const key = `${off.dx},${off.dy}`;
      this.isPainting = true;
      this.paintValue = !this.blocked.has(key); // primer click define el modo (agregar/borrar)
      this.applyPaint(key);
    });
    this.canvas.addEventListener('mousemove', (ev) => {
      if (!this.isPainting) return;
      const off = toOffset(ev);
      if (!off) return;
      this.applyPaint(`${off.dx},${off.dy}`);
    });
    const stop = (): void => {
      this.isPainting = false;
    };
    this.canvas.addEventListener('mouseup', stop);
    this.canvas.addEventListener('mouseleave', stop);
  }

  private applyPaint(key: string): void {
    const has = this.blocked.has(key);
    if (this.paintValue && !has) {
      this.blocked.add(key);
    } else if (!this.paintValue && has) {
      this.blocked.delete(key);
    } else {
      return;
    }
    this.refreshCount();
    this.render();
  }

  // ── Guardado / estado ──────────────────────────────────────────────────────

  private async save(btn: HTMLButtonElement): Promise<void> {
    if (!this.currentId) return;
    const blocks: HouseBlockOffset[] = [...this.blocked].map((k) => {
      const [dx, dy] = k.split(',').map(Number);
      return { dx, dy };
    });
    const original = btn.textContent;
    btn.disabled = true;
    try {
      await saveHouseCollision(this.currentId, blocks);
      this.serverCatalog[this.currentId] = blocks;
      btn.textContent = '¡Guardado!';
      this.setStatus(`Guardado ${this.currentId} (${blocks.length} tiles).`);
    } catch (err) {
      btn.textContent = 'Error';
      this.setStatus(`Error al guardar: ${(err as Error).message}`, true);
    } finally {
      window.setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1000);
    }
  }

  private refreshCount(): void {
    this.countEl.textContent = `${this.blocked.size} tiles`;
  }

  private setStatus(msg: string, isError = false): void {
    this.statusEl.textContent = msg;
    this.statusEl.style.color = isError ? '#ff8a8a' : '#9ad29a';
  }
}
